/**
 * Live chat-feed preview: subscribe to the stream's WebSocket and collect the
 * `chat.message` events the API broadcasts as it ingests chat. Ephemeral by
 * design — there's no backfill; the feed fills as messages arrive and keeps only
 * the most recent `MAX_MESSAGES` so a busy chat can't grow it unbounded.
 *
 * This opens its own socket (the Settings tab has no queue subscription); the
 * server fans every stream event out to all of a stream's connections, and we
 * simply ignore the `queue.updated` frames here.
 */
import { useCallback, useEffect, useState } from "react";
import type { ChatFeedMessage } from "../lib/api";
import { useChannelSocket } from "./useChannelSocket";

const MAX_MESSAGES = 100;

export function useChatFeed(channelId: string) {
  const [messages, setMessages] = useState<ChatFeedMessage[]>([]);

  // Reset the buffer when the watched stream changes.
  useEffect(() => setMessages([]), [channelId]);

  const onMessage = useCallback((msg: unknown) => {
    const { type, channelId: msgChannelId, message } = (msg ?? {}) as {
      type?: string;
      channelId?: string;
      message?: ChatFeedMessage;
    };
    if (type === "chat.message" && msgChannelId === channelId && message) {
      setMessages((prev) => [...prev, message].slice(-MAX_MESSAGES));
    }
  }, [channelId]);
  const { live } = useChannelSocket(channelId, onMessage);

  return { messages, live };
}
